import React, { useState } from 'react';
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/solid';
import { SearchParams } from '../../pages/browse-listings/BrowseListingsPage';
import SearchBar from './SearchBar';

interface CompactSearchBarProps {
  searchParams: SearchParams;
  setSearchParams: React.Dispatch<React.SetStateAction<SearchParams>>;
  onSearch: (params: SearchParams) => void;
}

/**
 * Compact search bar for mobile devices
 * Shows a compact button that expands to full search when clicked
 * Similar to Airbnb's mobile search experience
 */
const CompactSearchBar: React.FC<CompactSearchBarProps> = ({
  searchParams,
  setSearchParams,
  onSearch
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const handleExpand = () => {
    setIsExpanded(true);
    // Prevent body scroll when search is expanded
    document.body.style.overflow = 'hidden';
  };

  const handleClose = () => {
    setIsExpanded(false);
    // Restore body scroll
    document.body.style.overflow = 'unset';
  };

  const handleSearchWithClose = (params: SearchParams) => {
    onSearch(params);
    handleClose();
  };

  if (isExpanded) {
    return (
      <div className="fixed inset-0 z-50 bg-white overflow-y-auto">
        {/* Header with close button */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between shadow-sm">
          <h2 className="text-lg font-semibold text-gray-800">
            Search properties
          </h2>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            aria-label="Close search"
          >
            <XMarkIcon className="w-6 h-6 text-gray-600" />
          </button>
        </div>

        {/* Full search bar */}
        <div className="p-4">
          <SearchBar
            searchParams={searchParams}
            setSearchParams={setSearchParams}
            onSearch={handleSearchWithClose}
          />
        </div>
      </div>
    );
  }

  // Compact view - shows summary of current search
  return (
    <button
      onClick={handleExpand}
      className="w-full bg-white rounded-full shadow-md border border-gray-200 px-6 py-3 flex items-center justify-center hover:shadow-lg transition-shadow"
    >
      <MagnifyingGlassIcon className="w-4 h-4 text-gray-600 flex-shrink-0 mr-2" />
      <span className="text-sm font-medium text-gray-800 truncate">
        {searchParams.location ? (
          <>
            {searchParams.location}
            {searchParams.propertyType !== 'Any' &&
              ` · ${searchParams.propertyType}`}
            {searchParams.bedrooms > 0 && ` · ${searchParams.bedrooms} bed`}
            {searchParams.minPrice > 0 || searchParams.maxPrice > 0
              ? ` · €${searchParams.minPrice || 0}-${searchParams.maxPrice || 'Any'}`
              : ''}
          </>
        ) : (
          'Start your search'
        )}
      </span>
    </button>
  );
};

export default CompactSearchBar;
